import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * Carga de una tabla de promovidos: pide `fetcher(params)` cada vez que cambian
 * los parámetros y expone loading/error/refresh. Cada tabla usa su propia
 * instancia, así que filtrar o refrescar una no toca la otra.
 *
 * `onLoaded(data)` se llama al resolver cada carga exitosa, en el mismo punto
 * del fetch (no en un efecto sobre `data`): no agrega renders ni re-ejecuta la
 * carga si el padre pasa un callback nuevo en cada render.
 */
export function usePromotedFetch(fetcher, params, onLoaded) {
    const [state, setState] = useState({
        loading: true,
        error: false,
        data: null,
    });
    const mounted = useRef(true);
    const lastRequest = useRef(0);
    const onLoadedRef = useRef(onLoaded);

    // El ref se actualiza después del commit, nunca durante el render
    // (advanced-use-latest). La carga lo lee al resolver, siempre después.
    useEffect(() => {
        onLoadedRef.current = onLoaded;
    }, [onLoaded]);

    useEffect(
        () => () => {
            mounted.current = false;
        },
        [],
    );

    const load = useCallback(async () => {
        const requestId = ++lastRequest.current;
        setState((previous) => ({...previous, loading: true}));
        try {
            const data = await fetcher(params);
            // Una respuesta vieja no pisa la de un filtro más reciente.
            if (!mounted.current || requestId !== lastRequest.current) return;
            setState({loading: false, error: false, data});
            if (onLoadedRef.current) onLoadedRef.current(data);
        } catch (e) {
            if (!mounted.current || requestId !== lastRequest.current) return;
            setState((previous) => ({
                ...previous,
                loading: false,
                error: true,
            }));
        }
    }, [fetcher, params]);

    useEffect(() => {
        load();
    }, [load]);

    const dismissError = useCallback(
        () => setState((previous) => ({...previous, error: false})),
        [],
    );

    return {...state, refresh: load, dismissError};
}
